import { getSelectorsByUserAgent } from 'react-device-detect';

export type OS = 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'chromeos' | 'unknown';

export type Browser = 'chrome' | 'safari' | 'firefox' | 'edge' | 'opera' | 'unknown';

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

export interface DeviceInfo {
	os: OS;
	browser: Browser;
	deviceType: DeviceType;
	isIOS: boolean;
	isAndroid: boolean;
	isMacOS: boolean;
	isWindows: boolean;
	isLinux: boolean;
	isMobile: boolean;
	isTablet: boolean;
	isDesktop: boolean;
	isSafari: boolean;
	isChrome: boolean;
	isFirefox: boolean;
	isEdge: boolean;
	/** iOS Safari specifically — not Chrome/Firefox/Edge on iOS, and not desktop Safari. */
	isMobileSafari: boolean;
	isIOSChrome: boolean;
	isIOSFirefox: boolean;
	isIOSEdge: boolean;
	/** An iOS web view that does not identify as a known browser, e.g. an in-app browser. */
	isIOSWebkit: boolean;
	/** In-app browser of a social/ad platform, where cookies and deep links behave differently. */
	isMobileAdBrowser: boolean;
}

/**
 * Server-render safe default. Every flag is false and every enum is 'unknown'
 * except `deviceType`, which has no neutral value and falls back to 'desktop'.
 */
export const DEFAULT_DEVICE_INFO: DeviceInfo = {
	os: 'unknown',
	browser: 'unknown',
	deviceType: 'desktop',
	isIOS: false,
	isAndroid: false,
	isMacOS: false,
	isWindows: false,
	isLinux: false,
	isMobile: false,
	isTablet: false,
	isDesktop: false,
	isSafari: false,
	isChrome: false,
	isFirefox: false,
	isEdge: false,
	isMobileSafari: false,
	isIOSChrome: false,
	isIOSFirefox: false,
	isIOSEdge: false,
	isIOSWebkit: false,
	isMobileAdBrowser: false,
};

const AD_BROWSER_PATTERN =
	/FBAN|FBAV|FB_IAB|Instagram|LinkedIn|Twitter|BytedanceWebview|musical_ly/i;

/**
 * iPadOS 13+ reports a desktop macOS user agent. The only way to tell an iPad
 * apart from a Mac is the touch capability, which is not in the user agent.
 */
const isIPadMasqueradingAsMac = (platform: string, maxTouchPoints: number) =>
	/^Mac/i.test(platform) && maxTouchPoints > 1;

/** The subset of `getSelectorsByUserAgent`'s untyped result that we rely on. */
interface UserAgentSelectors {
	osName?: string;
	isIOS?: boolean;
	isAndroid?: boolean;
	isMacOs?: boolean;
	isWindows?: boolean;
	isMobile?: boolean;
	isTablet?: boolean;
	isDesktop?: boolean;
	isSafari?: boolean;
	isMobileSafari?: boolean;
	isChrome?: boolean;
	isFirefox?: boolean;
	isEdge?: boolean;
	isOpera?: boolean;
}

const toOS = (osName: string | undefined): OS => {
	switch (osName) {
		case 'iOS':
			return 'ios';
		case 'Android':
			return 'android';
		case 'Mac OS':
			return 'macos';
		case 'Windows':
			return 'windows';
		case 'Linux':
			return 'linux';
		case 'Chromium OS':
		case 'Chrome OS':
			return 'chromeos';
		default:
			return 'unknown';
	}
};

const toBrowser = (selectors: UserAgentSelectors): Browser => {
	if (selectors.isChrome) {
		return 'chrome';
	}
	if (selectors.isSafari) {
		return 'safari';
	}
	if (selectors.isFirefox) {
		return 'firefox';
	}
	if (selectors.isEdge) {
		return 'edge';
	}
	if (selectors.isOpera) {
		return 'opera';
	}
	return 'unknown';
};

type DeviceClass = Pick<
	DeviceInfo,
	'os' | 'deviceType' | 'isIOS' | 'isMacOS' | 'isMobile' | 'isTablet' | 'isDesktop'
>;

/**
 * `isIPadOS` corrects for iPadOS 13+, which reports a desktop macOS user agent.
 * Such a device parses as a macOS desktop, so every platform flag it touches
 * has to be re-derived.
 */
const toDeviceClass = (selectors: UserAgentSelectors, isIPadOS: boolean): DeviceClass => {
	if (isIPadOS) {
		return {
			os: 'ios',
			deviceType: 'tablet',
			isIOS: true,
			isMacOS: false,
			isMobile: true,
			isTablet: true,
			isDesktop: false,
		};
	}

	const isTablet = Boolean(selectors.isTablet);
	const isMobile = Boolean(selectors.isMobile);

	let deviceType: DeviceType = 'desktop';
	if (isTablet) {
		deviceType = 'tablet';
	} else if (isMobile) {
		deviceType = 'mobile';
	}

	return {
		os: toOS(selectors.osName),
		deviceType,
		isIOS: Boolean(selectors.isIOS),
		isMacOS: Boolean(selectors.isMacOs),
		isMobile,
		isTablet,
		isDesktop: Boolean(selectors.isDesktop),
	};
};

/**
 * Derives device, OS and browser information from a user agent string.
 *
 * Pure by design: it takes everything it needs as arguments so it can be unit
 * tested and reused wherever a user agent is available. Prefer `useDeviceInfo`
 * in components — user agent driven markup must not be server rendered,
 * because the WAC edge cache key does not include the user agent.
 *
 * @param userAgent `navigator.userAgent`, or the `user-agent` request header
 * @param platform `navigator.platform` — needed to identify iPadOS 13+
 * @param maxTouchPoints `navigator.maxTouchPoints` — needed to identify iPadOS 13+
 */
export const detectDeviceInfo = (
	userAgent: string,
	platform = '',
	maxTouchPoints = 0,
): DeviceInfo => {
	// getSelectorsByUserAgent logs an error and returns undefined for a blank
	// user agent, so short circuit rather than let it report a false positive.
	if (!userAgent) {
		return { ...DEFAULT_DEVICE_INFO };
	}

	const selectors: UserAgentSelectors | undefined = getSelectorsByUserAgent(userAgent);
	if (!selectors) {
		return { ...DEFAULT_DEVICE_INFO };
	}

	const isIPadOS = Boolean(selectors.isMacOs) && isIPadMasqueradingAsMac(platform, maxTouchPoints);
	const deviceClass = toDeviceClass(selectors, isIPadOS);
	const browser = toBrowser(selectors);
	const { isIOS } = deviceClass;

	return {
		...deviceClass,
		browser,
		isAndroid: Boolean(selectors.isAndroid),
		isWindows: Boolean(selectors.isWindows),
		isLinux: deviceClass.os === 'linux',
		isSafari: browser === 'safari',
		isChrome: browser === 'chrome',
		isFirefox: browser === 'firefox',
		isEdge: browser === 'edge',
		isMobileSafari: Boolean(selectors.isMobileSafari) || (isIPadOS && browser === 'safari'),
		isIOSChrome: isIOS && browser === 'chrome',
		isIOSFirefox: isIOS && browser === 'firefox',
		isIOSEdge: isIOS && browser === 'edge',
		isIOSWebkit: isIOS && browser === 'unknown',
		isMobileAdBrowser: AD_BROWSER_PATTERN.test(userAgent),
	};
};
